import { type CreditCardNetwork } from '@wix/cashier-common/dist/src/enums/payments/CreditCardNetwork';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import React, { Component, type Context, createRef } from 'react';
import { checkoutBuyerFillsFormFields } from '@wix/bi-logger-payments-data/v2';
import { biLogger } from '../../utils/biLogger';
import { PaymentProviderContext } from '../../contexts/PaymentProvider';
import { square } from '../../providers/square';
import { withoutSpaces } from '../../server/utils/withoutSpaces';
import { type Expiration } from '../../types/Expiration';
import { type Fields } from '../../types/Field';
import { FieldName } from '../../types/CardFieldName';
import { type ValidateResult } from '../../types/ValidateResult';
import { captureIfThrows } from '../../utils/errorReporter';
import { filterDefined } from '../../utils/filterDefined';
import { type CardData, type CardFormAPI, type FormRefs, type SavedCardData } from './types';
import { getCardNetworkByNumber } from './utils/cardNetworksMeta';
import { createFieldsStates, createFieldState } from './utils/createFieldState';
import { transformExpireObjToStr } from './utils/formatting';
import { isFieldFinished, validateField } from './utils/validation';
import { CardFormView } from './view';

export interface CardFormControllerProps {
  fields: FieldName[];
  savedCardNetwork?: CreditCardNetwork;
  savedCardExpiration?: Expiration;
  onApiInit(api: CardFormAPI): void;
  onChange(validityState: ValidateResult): void;
}

export interface CardFormControllerState {
  fields: Fields;
  cardNetwork: CreditCardNetwork;
}

export class CardFormController extends Component<CardFormControllerProps, CardFormControllerState> {
  readonly formRefs: FormRefs;
  readonly fieldsOrder: FieldName[];
  static contextType: Context<PaymentProvider> = PaymentProviderContext;

  static getDerivedStateFromProps(props: CardFormControllerProps, state: CardFormControllerState) {
    if (!state.fields.hasOwnProperty(FieldName.CVV) && props.fields.includes(FieldName.CVV)) {
      return {
        fields: {
          ...state.fields,
          [FieldName.CVV]: createFieldState(),
        },
      };
    }

    return null;
  }

  constructor(props: CardFormControllerProps) {
    super(props);

    this.fieldsOrder = [
      FieldName.Number,
      FieldName.ExpireDate,
      FieldName.CVV,
      FieldName.HolderName,
      FieldName.PostalCode,
    ].filter((fieldName) => props.fields.includes(fieldName));

    this.formRefs = this.fieldsOrder.reduce<FormRefs>((formRefs, fieldName: FieldName) => {
      formRefs[fieldName] = createRef();
      return formRefs;
    }, {});

    const fields = createFieldsStates(props.fields);

    if (props.fields.includes(FieldName.ExpireDate) && props.savedCardExpiration) {
      const expireDateValue = transformExpireObjToStr(props.savedCardExpiration);
      fields[FieldName.ExpireDate] = {
        ...fields[FieldName.ExpireDate],
        value: expireDateValue,
        isValid: false,
        isTouched: true,
        isEmpty: expireDateValue.length === 0,
      };
    }

    this.state = {
      cardNetwork: props.savedCardNetwork,
      fields,
    };
  }

  api: CardFormAPI = {
    isValid: () => this.isFormValid(),
    validate: () => {
      this.setState(({ fields }) => ({
        fields: Object.entries(fields).reduce(
          (acc, [fieldName, fieldValue]) => {
            acc[fieldName] = {
              ...fieldValue,
              isTouched: true,
              isErrorCanBeShown: true,
            };

            return acc;
          },
          { ...fields },
        ),
      }));
      const isValid: boolean = this.isFormValid();

      return {
        isValid,
        ...(!isValid && {
          invalidFields: this.getInvalidFields(),
        }),
      };
    },
    getCardData: (): CardData => {
      const { fields, cardNetwork } = this.state;

      const cardData: CardData = {
        number: withoutSpaces(fields[FieldName.Number].value),
        network: cardNetwork,
        holderName: fields[FieldName.HolderName].value,
      };

      if (fields[FieldName.ExpireDate]) {
        cardData.expiration = withoutSpaces(fields[FieldName.ExpireDate].value);
      }

      if (fields[FieldName.CVV]) {
        cardData.cvv = fields[FieldName.CVV].value;
      }

      return cardData;
    },
    getSavedCardData: (): SavedCardData => {
      const { fields } = this.state;
      const savedCardData: SavedCardData = {};

      if (fields[FieldName.ExpireDate]) {
        savedCardData.expiration = withoutSpaces(fields[FieldName.ExpireDate].value);
      }

      if (fields[FieldName.CVV]) {
        savedCardData.cvv = fields[FieldName.CVV].value;
      }

      return savedCardData;
    },
    focusInvalidField: (fieldName: FieldName): void => {
      this.focusField(fieldName);

      this.setState(
        {
          fields: {
            ...this.state.fields,
            [fieldName]: {
              ...this.state.fields[fieldName],
              isValid: false,
              isTouched: true,
              isFinished: true,
              isErrorCanBeShown: true,
            },
          },
        },
        () => {
          this.reportChange();
        },
      );
    },
  };

  isFormValid = () => {
    return Object.values(this.state.fields).every(({ isValid }) => isValid);
  };

  getInvalidFields = (): FieldName[] => {
    return Object.entries(this.state.fields)
      .filter(([fieldName, fieldData]) => !fieldData.isValid)
      .map(([fieldName]) => fieldName as FieldName);
  };

  reportChange = () => {
    const isValid: boolean = this.isFormValid();

    this.props.onChange({
      isValid,
      ...(!isValid && { invalidFields: this.getInvalidFields() }),
    });
  };

  focusFieldAfter = (currentFieldName: FieldName): void => {
    const nextFieldName = this.getNextField(currentFieldName);

    if (nextFieldName) {
      this.focusField(nextFieldName);
    }
  };

  getNextField(fieldName: FieldName): FieldName {
    const index = this.fieldsOrder.indexOf(fieldName);
    return this.fieldsOrder[index + 1];
  }

  focusField = (fieldName: FieldName): void => {
    this.context === PaymentProvider.Square
      ? square.api.focusField(fieldName)
      : this.formRefs[fieldName].current.focus();
  };

  handleFieldChange = captureIfThrows(
    (fieldName: FieldName, value: string, cb?: () => void, allowAutoBlurIfValid = true): void => {
      const { cardNetwork } = this.state;
      const isValid: boolean = validateField(fieldName, {
        value,
        ...(fieldName === FieldName.CVV && {
          cardNetwork,
        }),
      });

      const isFinished: boolean = isFieldFinished({ fieldName, value, cardNetwork: this.state.cardNetwork });

      if (
        isValid &&
        allowAutoBlurIfValid &&
        (fieldName === FieldName.ExpireDate ||
          fieldName === FieldName.Number ||
          (fieldName === FieldName.CVV &&
            (cardNetwork || value.length === 4) &&
            this.state.fields.cardCvv.value.length < value.length))
      ) {
        this.focusFieldAfter(fieldName);
      }

      this.setState(
        (state: CardFormControllerState) => {
          const nextFields: Fields = { ...state.fields };

          nextFields[fieldName] = {
            ...nextFields[fieldName],
            value,
            isValid,
            isFinished,
            isErrorCanBeShown: false,
            isEmpty: value.length === 0,
          };

          if (fieldName === FieldName.Number) {
            nextFields[FieldName.CVV] = {
              ...nextFields[FieldName.CVV],
              isValid: validateField(FieldName.CVV, {
                value: nextFields[FieldName.CVV].value,
                cardNetwork: getCardNetworkByNumber(value),
              }),
              isFinished: isFieldFinished({
                fieldName: FieldName.CVV,
                value: nextFields[FieldName.CVV].value,
                cardNetwork: getCardNetworkByNumber(value),
              }),
              isEmpty: nextFields[FieldName.CVV].value.length === 0,
              isErrorCanBeShown: nextFields[FieldName.CVV].value.length > 0,
            };
          }

          return {
            ...(fieldName === FieldName.Number && {
              cardNetwork: getCardNetworkByNumber(value),
            }),
            fields: nextFields,
          };
        },
        () => {
          this.reportChange();
          if (cb) {
            cb();
          }
        },
      );
    },
  );

  handleFieldFocus = captureIfThrows((fieldName: FieldName, isAutoFill?: boolean): void => {
    const { isValid, isEmpty } = this.state.fields[fieldName];

    biLogger.report(
      checkoutBuyerFillsFormFields(
        filterDefined({
          actionType: 'start',
          mandatoryField: fieldName,
          isValid,
          isEmpty,
          isAutoFill,
        }),
      ),
    );
  });

  handleFieldBlur = captureIfThrows((fieldName: FieldName, isAutoFill?: boolean): void => {
    this.setState(
      (state: CardFormControllerState) => ({
        fields: {
          ...state.fields,
          [fieldName]: {
            ...state.fields[fieldName],
            isTouched: true,
            isErrorCanBeShown: this.state.fields[fieldName].isEmpty ? false : !this.state.fields[fieldName].isValid,
          },
        },
      }),
      () => {
        const { isValid, isEmpty } = this.state.fields[fieldName];

        biLogger.report(
          checkoutBuyerFillsFormFields(
            filterDefined({
              actionType: 'finish',
              mandatoryField: fieldName,
              isValid,
              isEmpty,
              isAutoFill,
            }),
          ),
        );
      },
    );
  });

  handleSquareFieldChange = captureIfThrows((fieldName: FieldName, isValid: boolean, isEmpty: boolean) => {
    this.setState(
      (prevState) => ({
        fields: {
          ...prevState.fields,
          [fieldName]: {
            ...prevState.fields[fieldName],
            isValid,
            isEmpty,
          },
        },
      }),
      this.reportChange,
    );
  });

  handleSquareCardNetworkChange = (cardNetwork: CreditCardNetwork) => {
    this.setState({ cardNetwork });
  };

  onFieldLabelClick = captureIfThrows((fieldName: FieldName): void => {
    if (this.context === PaymentProvider.Square) {
      square.api.focusField(fieldName);
    }
  });

  componentDidMount() {
    this.props.onApiInit(this.api);

    if (this.context === PaymentProvider.Square) {
      square.api.listenFieldChange(this.handleSquareFieldChange);
      square.api.listenCardNetworkChange(this.handleSquareCardNetworkChange);
    }
  }

  componentWillUnmount() {
    if (this.context === PaymentProvider.Square) {
      square.api.unlistenFieldChange(this.handleSquareFieldChange);
      square.api.unlistenCardNetworkChange(this.handleSquareCardNetworkChange);
    }
  }

  render() {
    return (
      <CardFormView
        fields={this.state.fields}
        cardNetwork={this.state.cardNetwork}
        onFieldChange={this.handleFieldChange}
        onFieldBlur={this.handleFieldBlur}
        onFieldFocus={this.handleFieldFocus}
        formRefs={this.formRefs}
        onFieldLabelClick={this.onFieldLabelClick}
      />
    );
  }
}
